import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import React, { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

// Models
import { QuoteHeaderAnswerInformationResponse, QuoteSupplierItemPartResponse } from "@/main/models/response";

// Redux
import { useWarehouseReducer } from "@/main/store/warehouse";

// Utils
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { getLabelTranslation } from "../../createAnswerScreen";
import { validateAnsweredItemForEdit } from "../../createAnswerSchema";

// Styles
import * as S from "../styles";

// Components
import { quoteSupplierItemApi } from "@/main/api";
import useAsyncModalConfirm from "@/main/components/shared/asyncModalConfirm/asyncModalConfirm";
import { KOGTableRow } from "@/main/components/shared/boxGrid/styles";
import RenderTooltipMessage from "@/main/components/shared/renderTooltipMessage/renderTooltipMessage";
import { DefaultValueParameterEnum } from "@/main/services/enum/DefaultValueParameterEnum";
import Image from "@mui/icons-material/Image";
import { IconButton } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ButtonDetailsPart from "../../components/buttonDetailsPart/buttonDetailsPart";
import LockedNotQuoteCheckbox from "../../components/lockedNotQuoteCheckbox/lockedNotQuoteCheckbox";
import {
	formatResponseToForm,
	generateRequestSaveCreateAnswer,
	isFinishedTimeWithAnswered,
} from "../../utilscreateAnswer";
import AreaButtonsEditPart from "./areaButtonsEditPart/areaButtonsEditPart";
import ColumnBackOrder from "./columnsAnswers/columnBackOrder";
import ColumnDimensions from "./columnsAnswers/columnDimensions";
import ColumnNotQuote from "./columnsAnswers/columnNotQuote";
import ColumnPartName from "./columnsAnswers/columnPartName";
import ColumnPartNumber from "./columnsAnswers/columnPartNumber";
import ColumnPickupDays from "./columnsAnswers/columnPickupDays";
import ColumnPrice from "./columnsAnswers/columnPrice";
import ColumnReason from "./columnsAnswers/columnReason";
import ColumnSku from "./columnsAnswers/columnSku";
import ColumnStock from "./columnsAnswers/columnStock";
import ColumnWarehouse from "./columnsAnswers/columnWarehouse";
import ColumnWeight from "./columnsAnswers/columnWeight";
import { useVisualIdentityReducer } from "@/main/store/visualIdentity";
interface ITableRowAnswer {
	row: QuoteSupplierItemPartResponse;
	fieldArrayName: "pendingItems" | "answeredItems";
	rowIndex: number;
	hasReadOnly: boolean;
	dataHeader?: QuoteHeaderAnswerInformationResponse;
	setAllSelected: React.Dispatch<React.SetStateAction<boolean>>;
	allSelected: boolean;
	isFinishedTime?: boolean;
	loadGrid?: (guidQuote: string, guidSupplier: string, idQuoteSuppItemStatus: number | null) => void;
	handleOpenModalDetails: (guidPart: string) => void;
	setHavePartInventory?: React.Dispatch<React.SetStateAction<boolean>>;
	dimensionsValidationError?: boolean;
	hidden?: boolean;
	hideGroupedCells?: boolean;
	groupedRowSpan?: number;
	partTypeContent?: React.ReactNode;
	disabledGroupFields?: boolean;
	isShowModalReasonAll?: boolean;
	setIsShowModalReasonAll?: React.Dispatch<React.SetStateAction<boolean>>;
	handleOpenModalImages: (item: QuoteSupplierItemPartResponse, allowEditing: boolean) => void;
	singleActionColumn?: boolean;
	hasActionColumn?: boolean;
}

function TableRowAnswer({
	row,
	fieldArrayName,
	rowIndex,
	hasReadOnly,
	dataHeader,
	loadGrid = () => {},
	setAllSelected,
	allSelected,
	handleOpenModalDetails,
	setHavePartInventory,
	isFinishedTime,
	dimensionsValidationError = false,
	hidden = false,
	hideGroupedCells = false,
	groupedRowSpan,
	partTypeContent,
	disabledGroupFields = false,
	isShowModalReasonAll,
	setIsShowModalReasonAll,
	handleOpenModalImages,
	singleActionColumn = false,
	hasActionColumn = false,
}: ITableRowAnswer) {
	const fieldPrefix = `${fieldArrayName}.${rowIndex}` as const;
	const [searchParams, _] = useSearchParams();
	const guidQuoteParams = searchParams.get("guidQuote") || "";
	const [isEdit, setIsEdit] = useState(false);
	const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();
	const [isLoadingSaveEdit, setIsLoadingSaveEdit] = useState(false);
	const methods = useFormContext();

	const { warehouse: warehouseReducer } = useWarehouseReducer();
	const haveOnlyOneWarehouse = warehouseReducer?.data?.length === 1;

	const { visualIdentity } = useVisualIdentityReducer();

	function validateEditedPart(): boolean {
		const formValuePart = methods.getValues(fieldPrefix);
		methods.clearErrors(fieldPrefix);

		const validation = validateAnsweredItemForEdit(formValuePart);
		if (validation.success) return true;

		const invalidFields: string[] = [];
		validation.error.issues.forEach((issue) => {
			const fieldName = issue.path.join(".");
			if (!fieldName) return;
			methods.setError(`${fieldPrefix}.${fieldName}` as never, {
				type: "custom",
				message: issue.message,
			});
			invalidFields.push(getLabelTranslation(String(issue.path[0])));
		});

		const namePart = row.quoteProcurementPartInfo.partName;
		const messageError = getTranslateByKey("information.fieldsInvalidToast", true)
			.replace("{0}", namePart || "")
			.replace("{1}", Array.from(new Set(invalidFields)).join(", "));
		toast.error(messageError);

		return false;
	}

	async function handleSubmitEditPart() {
		const formValuePart = methods.getValues(fieldPrefix);
		if (!validateEditedPart()) return;

		const isPickupTimeGreater =
			parseInt(formValuePart.pickupTime, 10) >= DefaultValueParameterEnum.CreateAnswerPickupTimeLimit;

		if (isPickupTimeGreater) {
			const message = getTranslateByKey("information.longPickupDays");
			const isAcepptedModal = await getConfirmation(message);
			if (!isAcepptedModal) return;
		}

		setIsLoadingSaveEdit(true);

		const formattedData = {
			[formValuePart.idUnique]: formValuePart,
		};
		if (!dataHeader) return;

		const request = generateRequestSaveCreateAnswer({
			data: formattedData,
			dataGridPending: [row],
			guidAddress: dataHeader.guidAddress,
			idQuoteSuppItemStatus: dataHeader.idQuoteSuppItemStatus,
			isEditAnswered: true,
		});

		quoteSupplierItemApi
			.save(request)
			.then(() => {
				toast.success(getTranslateByKey("information.savedSuccess"));
				loadGrid?.(guidQuoteParams, dataHeader.guidSupplier, dataHeader.idQuoteSuppItemStatus);
			})
			.finally(() => {
				setIsLoadingSaveEdit(false);
			});
	}

	function handleCancelEdit() {
		setIsEdit(false);
		methods.setValue(fieldPrefix, formatResponseToForm(row, true));
	}

	useEffect(() => {
		if (!isNullUndefined(row.guidSupplierInventory) && !hidden) {
			setHavePartInventory?.(true);
		}
	}, []);

	useEffect(() => {
		if (isFinishedTime) {
			setIsEdit(false);
		}
	}, [isFinishedTime]);

	const isQuoteClosed = Boolean(isFinishedTime || (dataHeader && isFinishedTimeWithAnswered(dataHeader)));
	const isSelfSupplied = Boolean(dataHeader?.selfSupplied);
	const showIconEdit = hasReadOnly && !isEdit && !isQuoteClosed && !isSelfSupplied;
	const showFieldWithouEdit = hasReadOnly && !isEdit;
	const lockedNotQuoteTooltip = isSelfSupplied
		? getTranslateByKey("information.doNotQuoteBlockedTooltip")
		: "";
	const savedReasonTooltip = `${row.reason ?? ""}${row.reasonDescription ? ` (${row.reasonDescription})` : ""}`;

	const disabledFieldsStyle: React.CSSProperties | undefined = disabledGroupFields
		? { pointerEvents: "none", opacity: 0.5 }
		: undefined;

	const watcherNotQuote = useWatch({
		control: methods.control,
		name: `${fieldPrefix}.notQuote`,
		defaultValue: false,
	});

	const hasImages = (row.images?.length ?? 0) > 0;
	const disabledImage = disabledGroupFields || watcherNotQuote || (isQuoteClosed && !hasImages);

	return (
		<KOGTableRow key={row.idUnique} $notClickable style={{ display: hidden ? "none" : undefined }}>
			{!hideGroupedCells && (
				<ColumnPartName row={row} rowSpan={groupedRowSpan} verticalAlign={groupedRowSpan ? "middle" : undefined} />
			)}
			{!hideGroupedCells && (
				<ColumnPartNumber
					quoteItemPartNumber={row.quoteProcurementPartInfo?.quoteItemPartNumber}
					rowSpan={groupedRowSpan}
					verticalAlign={groupedRowSpan ? "middle" : undefined}
				/>
			)}
			<S.TableCellGrid align="left">
				<S.PartTypeContainer>
					{partTypeContent ?? row.partTypeName}
					<IconButton
						disabled={disabledImage}
						onClick={() => handleOpenModalImages(row, (!hasReadOnly || isEdit || isSelfSupplied) && !isQuoteClosed)}
					>
						<S.ImageIconWrapper>
							<Image
								sx={{
									color: disabledImage ? visualIdentity.colors.grayDark : visualIdentity.colors.pantone2726C,
								}}
							/>
							<S.ImageBadge
								style={{
									backgroundColor: disabledImage ? visualIdentity.colors.grayDark : visualIdentity.colors.pantone2726C,
								}}
							>
								{disabledGroupFields ? 0 : (row.images?.length ?? 0)}
							</S.ImageBadge>
						</S.ImageIconWrapper>
					</IconButton>
				</S.PartTypeContainer>
			</S.TableCellGrid>
			<S.TableCellGrid align="left" $hideColumn={haveOnlyOneWarehouse}>
				<div style={disabledFieldsStyle}>
					<ColumnWarehouse fieldPrefix={fieldPrefix} showFieldWithouEdit={showFieldWithouEdit} row={row} />
				</div>
			</S.TableCellGrid>
			<S.TableCellGrid align="left" $showMobile>
				<div style={disabledFieldsStyle}>
					<ColumnSku fieldPrefix={fieldPrefix} showFieldWithouEdit={showFieldWithouEdit} row={row} />
				</div>
			</S.TableCellGrid>
			<S.TableCellGrid align="left">
				<div style={disabledFieldsStyle}>
					<ColumnStock fieldPrefix={fieldPrefix} showFieldWithouEdit={showFieldWithouEdit} row={row} />
				</div>
			</S.TableCellGrid>
			<S.TableCellInputNumber align="left">
				<div style={disabledFieldsStyle}>
					<ColumnPrice fieldPrefix={fieldPrefix} showFieldWithouEdit={showFieldWithouEdit} row={row} />
				</div>
			</S.TableCellInputNumber>
			<S.TableCellInputNumber align="left">
				<div style={disabledFieldsStyle}>
					<ColumnPickupDays fieldPrefix={fieldPrefix} showFieldWithouEdit={showFieldWithouEdit} row={row} />
				</div>
			</S.TableCellInputNumber>
			{!row.deliveryBySupplier && (
				<>
					<S.TableCellGrid align="left">
						<div style={disabledFieldsStyle}>
							<ColumnDimensions
								fieldPrefix={fieldPrefix}
								showFieldWithouEdit={showFieldWithouEdit}
								row={row}
								dimensionsValidationError={dimensionsValidationError}
							/>
						</div>
					</S.TableCellGrid>
					<S.TableCellInputNumber align="left">
						<div style={disabledFieldsStyle}>
							<ColumnWeight fieldPrefix={fieldPrefix} showFieldWithouEdit={showFieldWithouEdit} row={row} />
						</div>
					</S.TableCellInputNumber>
				</>
			)}
			<S.TableCellGrid align="center">
				<S.AreaCheckbox style={disabledFieldsStyle}>
					<ColumnBackOrder fieldPrefix={fieldPrefix} showFieldWithouEdit={showFieldWithouEdit} row={row} />
				</S.AreaCheckbox>
			</S.TableCellGrid>
			<S.TableCellGrid align="left">
				<S.AreaCheckbox>
					{!showFieldWithouEdit && (
						<ColumnNotQuote
							hasReadOnly={showFieldWithouEdit}
							fieldPrefix={fieldPrefix}
							fieldArrayName={fieldArrayName}
							setAllSelected={setAllSelected}
							row={row}
							lockedTooltipMessage={lockedNotQuoteTooltip}
						/>
					)}

					{isSelfSupplied && showFieldWithouEdit ? (
						row.notQuote && (
							<LockedNotQuoteCheckbox
								id={`id-createAnswer-checkBox-notQuote-${row.idUnique}`}
								checked
								tooltipMessage={savedReasonTooltip}
							/>
						)
					) : row.reason && showFieldWithouEdit ? (
						row.reason ? (
							<S.TextAlignTable $textAlign="center">
								{row.reason} {row.reasonDescription ? `(${row.reasonDescription})` : ""}
							</S.TextAlignTable>
						) : (
							""
						)
					) : (
						<ColumnReason
							hasReadOnly={showFieldWithouEdit}
							fieldPrefix={fieldPrefix}
							isShowModalReasonAll={isShowModalReasonAll}
							setIsShowModalReasonAll={setIsShowModalReasonAll}
							allSelected={allSelected}
						/>
					)}
				</S.AreaCheckbox>
			</S.TableCellGrid>

			{(singleActionColumn ||
				hasActionColumn ||
				showIconEdit ||
				row.showNoPriceWarning ||
				row.guidSupplierInventory ||
				isEdit) && (
				<S.TableCellGrid align={singleActionColumn ? "right" : "left"}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: singleActionColumn ? "flex-end" : "flex-start",
						}}
					>
						{row.showNoPriceWarning && (
							<IconButton
								onClick={() => {
									const buidMessageNoPrice = getTranslateByKey("information.noPriceInInventoryMessagePopUp");
									toast.warning(buidMessageNoPrice);
								}}
							>
								<WarningIcon style={{ color: "#CCCC00" }} />
							</IconButton>
						)}

						{showIconEdit && (
							<RenderTooltipMessage tooltipMessage={getTranslateByKey("information.edit")}>
								<IconButton
									id="id-createanswer-icon-button-edit-answered"
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										setIsEdit(true);
									}}
								>
									<EditIcon />
								</IconButton>
							</RenderTooltipMessage>
						)}
						{isEdit && (
							<AreaButtonsEditPart
								isLoadingSaveEdit={isLoadingSaveEdit}
								onClickConfirm={() => {
									handleSubmitEditPart();
								}}
								onClickCancel={() => {
									handleCancelEdit();
								}}
							/>
						)}
						<ButtonDetailsPart
							guidSupplierInventory={row.guidSupplierInventory}
							notQuote={row.notQuote}
							handleOpenModalDetails={handleOpenModalDetails}
						/>
					</div>
				</S.TableCellGrid>
			)}

			<ModalConfirmation />
		</KOGTableRow>
	);
}

export default TableRowAnswer;
